import type { LiveSubagentRosterState } from "@t3tools/client-runtime/state/server";
import type { EnvironmentId, LiveSubagentId, ThreadId } from "@t3tools/contracts";
import { Bot, CircleAlert, LoaderCircle } from "lucide-react";
import { useMemo } from "react";

import { i18n } from "~/i18n";
import { cn } from "~/lib/utils";
import { useEnvironmentQuery, type EnvironmentQueryView } from "~/state/query";
import { serverEnvironment } from "~/state/server";
import {
  liveSubagentStatusDotClass,
  liveSubagentStatusLabel,
  liveSubagentDisplayName,
  liveSubagentSummaryText,
  liveSubagentUnavailableMessage,
} from "./liveSubagentPresentation";

export function shouldShowLiveSubagentSection(input: {
  readonly isServerThread: boolean;
  readonly supportsRoster: boolean;
}): boolean {
  return input.isServerThread && input.supportsRoster;
}

export interface LiveSubagentRosterTarget {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
}

export function useLiveSubagentRoster(target: LiveSubagentRosterTarget | null) {
  const rosterAtom = useMemo(
    () =>
      target
        ? serverEnvironment.liveSubagentRoster({
            environmentId: target.environmentId,
            input: { threadId: target.threadId },
          })
        : null,
    [target?.environmentId, target?.threadId],
  );
  return useEnvironmentQuery(rosterAtom);
}

function SummaryMessage(props: {
  readonly title: string;
  readonly detail?: string;
  readonly icon?: "agent";
  readonly loading?: boolean;
}) {
  return (
    <div role="status" className="flex min-h-12 items-start gap-3 rounded-lg px-3 py-3 text-left">
      {props.loading ? (
        <LoaderCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      ) : props.icon === "agent" ? (
        <Bot className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      ) : (
        <CircleAlert className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      )}
      <span className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-foreground">{props.title}</p>
        {props.detail ? (
          <p className="mt-0.5 text-pretty text-[11px] leading-relaxed text-muted-foreground">
            {props.detail}
          </p>
        ) : null}
      </span>
    </div>
  );
}

export function LiveSubagentSummaryContent(props: {
  readonly roster: EnvironmentQueryView<LiveSubagentRosterState>;
  readonly onSelectAgent: (agentId: LiveSubagentId) => void;
}) {
  const { roster } = props;
  if (roster.error !== null) {
    return <SummaryMessage title={i18n.t("liveSubagents.transportError")} detail={roster.error} />;
  }
  if (roster.data === null) {
    return <SummaryMessage loading title={i18n.t("liveSubagents.connecting")} />;
  }
  if (roster.data.status === "expired") {
    return (
      <SummaryMessage
        title={i18n.t("liveSubagents.runtimeEnded")}
        detail={i18n.t("liveSubagents.runtimeEndedDescription")}
      />
    );
  }
  if (roster.data.status === "unavailable") {
    return (
      <SummaryMessage
        title={i18n.t("liveSubagents.agentsUnavailable")}
        detail={liveSubagentUnavailableMessage(roster.data.reason)}
      />
    );
  }
  if (roster.data.agents.length === 0) {
    return <SummaryMessage icon="agent" title={i18n.t("liveSubagents.empty")} />;
  }

  return (
    <ul
      className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
      aria-label={i18n.t("liveSubagents.rosterLabel")}
    >
      {roster.data.agents.map((agent) => {
        const name = liveSubagentDisplayName(agent);
        const status = liveSubagentStatusLabel(agent.phase);
        const summary = liveSubagentSummaryText(agent);
        return (
          <li key={agent.id}>
            <button
              type="button"
              aria-label={i18n.t("liveSubagents.openAgent", { name, status })}
              onClick={() => props.onSelectAgent(agent.id)}
              className="group flex min-h-10 w-full items-start gap-3 rounded-lg px-3 py-2 text-left outline-none hover:bg-accent/70 focus-visible:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span
                className={cn(
                  "mt-1.5 size-2 shrink-0 rounded-full",
                  liveSubagentStatusDotClass(agent.phase),
                )}
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">
                    {name}
                  </span>
                  <span className="shrink-0 text-[10px] text-muted-foreground">{status}</span>
                </span>
                {summary ? (
                  <span className="mt-0.5 block truncate text-[11px] leading-4 text-muted-foreground">
                    {summary}
                  </span>
                ) : null}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
